import type { GeometryElement as JXGElement } from 'jsxgraph';
import type { ThemeColorName } from '@examdojo/styles';
import type { PortMap, PointPort } from '../../../port';
import type { SolvedArc } from '../../../analytics';
import type { PlacedShape } from '../../../placement';
import type { GeometryPadElementContext, CreatedGeometryElement } from '../../element.model';
import {
  resolveGeometryStyle,
  resolveGeometryLabelColor,
  resolveSubpartLabelOverride,
  resolveAngleLabelOverride,
  computeEdgeScope,
  computeAngleScope,
} from '../../coloring';
import { initShapeCreatorContext } from '../shape-creator.util';
import type { ArcElement } from './arc.model';

interface SetAttr { setAttribute: (a: Record<string, unknown>) => void }
interface Coord { X: () => number; Y: () => number }

/**
 * Create an arc element on the board.
 *
 * Uses board.create('point') for structural points so that
 * updateElementPositions() can moveTo() them and the arc/radii/angle
 * follow automatically through JSXGraph's dependency graph.
 */
export function createArc(
  element: ArcElement & { id: string },
  placed: PlacedShape,
  solved: SolvedArc,
  context: GeometryPadElementContext,
): CreatedGeometryElement {
  const ctx = initShapeCreatorContext(element, context);
  const { elementFactory, elementDisplay, board, jsxObjects, labels, styleCtx, geometryConfig } = ctx;
  const center = placed.vertices['center'] as [number, number];
  const startPoint = placed.vertices['startPoint'] as [number, number];
  const endPoint = placed.vertices['endPoint'] as [number, number];
  const givenKeys = new Set(Object.keys(element.params));
  const names = element.names;
  const shapeDefaults = styleCtx.shapeTheme['Arc'];

  /** Resolve subpart visibility: explicit per-element override → shape theme default → fallback. */
  const subpartVisible = (name: string, fallback: boolean): boolean => {
    const explicit = elementDisplay?.subparts?.[name]?.visible;
    if (explicit !== undefined) { return explicit; }
    const themeVisible = shapeDefaults?.subparts?.[name]?.visible;
    if (themeVisible !== undefined) { return themeVisible; }
    return fallback;
  };

  const setFixed = (el: JXGElement) => {
    (el as unknown as SetAttr).setAttribute({ fixed: true, highlight: false });
  };

  // --- Structural points ---
  // These are the canonical vertex points that updateElementPositions() moves.
  // All other elements (arc, radii, angle, chord) reference these so they
  // update automatically via JSXGraph's dependency graph.
  const centerPtStyle = resolveGeometryStyle('point', 'Arc', styleCtx, elementDisplay, 'center');
  const centerVisible = subpartVisible('center', true);
  const pCenter = board.create('point', center, {
    name: 'center',
    fixed: true,
    highlight: false,
    visible: centerVisible,
    size: centerPtStyle.size ?? 4,
    color: centerPtStyle.color,
    withLabel: false,
  }) as JXGElement;

  const startPtStyle = resolveGeometryStyle('point', 'Arc', styleCtx, elementDisplay, 'start');
  const startVisible = subpartVisible('start', true);
  const pStart = board.create('point', startPoint, {
    name: 'start',
    fixed: true,
    highlight: false,
    visible: startVisible,
    size: startPtStyle.size ?? 4,
    color: startPtStyle.color,
    withLabel: false,
  }) as JXGElement;

  const endPtStyle = resolveGeometryStyle('point', 'Arc', styleCtx, elementDisplay, 'end');
  const endVisible = subpartVisible('end', true);
  const pEnd = board.create('point', endPoint, {
    name: 'end',
    fixed: true,
    highlight: false,
    visible: endVisible,
    size: endPtStyle.size ?? 4,
    color: endPtStyle.color,
    withLabel: false,
  }) as JXGElement;

  jsxObjects.push(pCenter, pStart, pEnd);

  // --- Arc curve (depends on structural points → auto-updates) ---
  const arcStyle = resolveGeometryStyle('circle', 'Arc', styleCtx, elementDisplay);
  const showSector = subpartVisible('sector', false);

  const arcResult = elementFactory.arc({
    center: pCenter,
    startPoint: pStart,
    endPoint: pEnd,
    style: {
      strokeColor: arcStyle.strokeColor,
      fillColor: arcStyle.fillColor,
      fillOpacity: arcStyle.fillOpacity,
      strokeWidth: arcStyle.strokeWidth,
      visible: elementDisplay?.visible ?? true,
      label: false,
    },
    sectorFill: showSector,
    sectorStyle: elementDisplay?.subparts?.['sector']?.polygon,
  });

  setFixed(arcResult.arc);
  setFixed(arcResult.center);
  setFixed(arcResult.startPoint);
  setFixed(arcResult.endPoint);
  jsxObjects.push(arcResult.arc, arcResult.center, arcResult.startPoint, arcResult.endPoint);
  if (arcResult.sector) {
    setFixed(arcResult.sector);
    jsxObjects.push(arcResult.sector);
  }

  // --- Radii (visible by default, use structural points for reactivity) ---
  const radiusLabelName = names?.r ?? 'r';
  const showRadiusStart = subpartVisible('radiusStart', true);
  if (showRadiusStart) {
    const radiusStyle = resolveGeometryStyle('line', 'Arc', styleCtx, elementDisplay, 'radiusStart');
    const labelColor = resolveGeometryLabelColor('Arc', styleCtx, elementDisplay, 'radiusStart') as ThemeColorName;
    const globalLabel = computeEdgeScope(
      'r',
      givenKeys,
      geometryConfig,
      labelColor,
      radiusLabelName !== 'r' ? radiusLabelName : undefined,
    );
    const label = resolveSubpartLabelOverride(radiusStyle.label, globalLabel, radiusLabelName, labelColor);
    const radiusResult = elementFactory.line({
      a: pCenter,
      b: pStart,
      name: radiusLabelName,
      style: { ...radiusStyle, label: label && { ...label, spacing: 'compact' }, vertex: { visible: false } },
    });
    setFixed(radiusResult.line);
    jsxObjects.push(radiusResult.line, radiusResult.a, radiusResult.b);
    jsxObjects.push(...radiusResult.helpers);
    labels.push(radiusResult.lineLabel);
  }

  const showRadiusEnd = subpartVisible('radiusEnd', true);
  if (showRadiusEnd) {
    const radiusStyle = resolveGeometryStyle('line', 'Arc', styleCtx, elementDisplay, 'radiusEnd');
    const radiusResult = elementFactory.line({
      a: pCenter,
      b: pEnd,
      name: radiusLabelName,
      style: { ...radiusStyle, label: false, vertex: { visible: false } },
    });
    setFixed(radiusResult.line);
    jsxObjects.push(radiusResult.line, radiusResult.a, radiusResult.b);
    jsxObjects.push(...radiusResult.helpers);
  }

  // --- Chord (hidden by default) ---
  const showChord = subpartVisible('chord', false);
  if (showChord) {
    const chordStyle = resolveGeometryStyle('line', 'Arc', styleCtx, elementDisplay, 'chord');
    const chordLabelName = names?.chordLength ?? 'chord';
    const labelColor = resolveGeometryLabelColor('Arc', styleCtx, elementDisplay, 'chord') as ThemeColorName;
    const globalLabel = computeEdgeScope('chordLength', givenKeys, geometryConfig, labelColor, chordLabelName);
    const label = resolveSubpartLabelOverride(chordStyle.label, globalLabel, chordLabelName, labelColor);
    const chordResult = elementFactory.line({
      a: pStart,
      b: pEnd,
      name: chordLabelName,
      style: { ...chordStyle, label: label && { ...label, spacing: 'compact' }, vertex: { visible: false } },
    });
    setFixed(chordResult.line);
    jsxObjects.push(chordResult.line, chordResult.a, chordResult.b);
    jsxObjects.push(...chordResult.helpers);
    labels.push(chordResult.lineLabel);
  }

  // --- Central angle marker (visible by default) ---
  const showAngle = subpartVisible('centralAngle', true);
  if (showAngle) {
    const angleStyle = resolveGeometryStyle('angle', 'Arc', styleCtx, elementDisplay, 'centralAngle');
    const angleLabelName = names?.centralAngle ?? 'θ';
    const angleLabelColor = resolveGeometryLabelColor(
      'Arc',
      styleCtx,
      elementDisplay,
      'centralAngle',
      'angle',
    ) as ThemeColorName;
    const scopeResult = computeAngleScope(
      { prop: 'centralAngle' },
      givenKeys,
      geometryConfig,
      angleLabelColor,
      angleLabelName,
    );
    const label = resolveAngleLabelOverride(angleStyle.label, scopeResult.label, angleLabelName, angleLabelColor);

    const angleResult = elementFactory.angle({
      a: pStart,
      vertex: pCenter,
      b: pEnd,
      name: angleLabelName,
      style: {
        fillColor: angleStyle.fillColor,
        fillOpacity: angleStyle.fillOpacity,
        strokeColor: angleStyle.strokeColor,
        type: 'sector',
        visible: true,
        label,
      },
    });
    jsxObjects.push(angleResult.angle);
    jsxObjects.push(...angleResult.helpers);
    labels.push(angleResult.label);
  }

  // --- Ports (use structural points for live coordinate tracking) ---
  const tCenter = pCenter as unknown as Coord;
  const tStart = pStart as unknown as Coord;
  const tEnd = pEnd as unknown as Coord;

  const ports: PortMap = {
    center: () => [tCenter.X(), tCenter.Y()] as PointPort,
    startPoint: () => [tStart.X(), tStart.Y()] as PointPort,
    endPoint: () => [tEnd.X(), tEnd.Y()] as PointPort,
    midPoint: () => {
      const cx = tCenter.X();
      const cy = tCenter.Y();
      const sx = tStart.X() - cx;
      const sy = tStart.Y() - cy;
      const ex = tEnd.X() - cx;
      const ey = tEnd.Y() - cy;
      const startAngle = Math.atan2(sy, sx);
      const endAngle = Math.atan2(ey, ex);
      let mid = (startAngle + endAngle) / 2;
      let delta = endAngle - startAngle;
      if (delta < 0) {
        delta += 2 * Math.PI;
      }
      if (delta > Math.PI) {
        mid += Math.PI;
      }
      const r = Math.sqrt(sx * sx + sy * sy);
      return [cx + r * Math.cos(mid), cy + r * Math.sin(mid)] as PointPort;
    },
    radius: () => {
      const sx = tStart.X() - tCenter.X();
      const sy = tStart.Y() - tCenter.Y();
      return Math.sqrt(sx * sx + sy * sy);
    },
    startAngle: () => solved.startAngleDeg,
    endAngle: () => solved.endAngleDeg,
    centralAngle: () => solved.centralAngleDeg,
    arcLength: () => solved.arcLength,
    chordLength: () => solved.chordLength,
    sectorArea: () => solved.sectorArea,
    segmentArea: () => solved.segmentArea,
    arc: () => arcResult.arc,
  };

  return {
    jsxObjects,
    labels,
    ports,
    vertexPoints: { center: pCenter, startPoint: pStart, endPoint: pEnd },
    destroy: () => {
      const b = elementFactory.getContext().board;
      for (const l of labels) {
        b.removeObject(l);
      }
      for (const obj of jsxObjects) {
        b.removeObject(obj);
      }
    },
  };
}
